import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import Navbar from "../../../ui/Navbar";
import { AuthContext } from "../../../../context/AuthContext";
import Table from "../../../ui/Table";
import Loader from "../../../ui/Loader";
import Modal from "../../../ui/Modal";
import { swalConfirm, swalError, swalSuccess } from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const statusOptions = [
  { value: "Active", label: "Active", icon: "🟢" },
  { value: "Completed", label: "Completed", icon: "✅" },
  { value: "Failed", label: "Failed", icon: "❌" },
];

const typeOptions = [
  { value: "", label: "All", icon: "📋" },
  { value: "Grader", label: "Grader", icon: "📝" },
  { value: "ScoreSummarizer", label: "Score Summarizer", icon: "📊" },
];

export default function GetAllBatchJobs() {
  const { id } = useParams();
  const isGlobal = id === "all";
  const { isAuthenticated, primaryRole } = useContext(AuthContext);
  const [institute, setInstitute] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("Active");
  const [selectedType, setSelectedType] = useState("");
  const [isBatchIdModalOpen, setIsBatchIdModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin" },
    {
      name: "Batch Jobs",
      path: `/superadmin/batch-jobs/${id}`,
      isLast: true,
    },
    {
      name: institute?.name || (isGlobal ? "All Institutes" : ""),
      path: `/superadmin/batch-jobs/${id}`,
      isLast: true,
    },
  ];

  const fetchInstituteDetails = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${id}`,
        { credentials: "include" },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch institute details");
      const data = await response.json();
      setInstitute(data);
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  const fetchBatchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (!isGlobal) params.append("instituteId", id);
      params.append("status", selectedStatus);
      if (selectedType) params.append("type", selectedType);

      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-all-external-batch-jobs?${params.toString()}`,
        { credentials: "include" },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch batch jobs");

      const result = await response.json();
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, isGlobal, selectedStatus, selectedType]);

  useEffect(() => {
    if (!isGlobal) {
      fetchInstituteDetails();
    }
  }, [fetchInstituteDetails, isGlobal]);

  useEffect(() => {
    fetchBatchJobs();
  }, [fetchBatchJobs]);

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/login" replace />;
  }

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        swalSuccess("Copied to clipboard!");
      })
      .catch((err) => {
        swalError("Failed to copy text: " + err);
      });
  };

  const showBatchId = (batchId) => {
    setSelectedBatchId(batchId);
    setIsBatchIdModalOpen(true);
  };

  const handleCancelBatch = async (batchId, actionType) => {
    let title = "Are you sure?";
    let text = "You won't be able to revert this!";
    let confirmBtnText = "Yes";

    if (actionType === "pure_cancel") {
      title = "Cancel Batch Completely?";
      text = "This will terminate the batch at the AI provider and cancel all queued requests. No papers will be evaluated.";
      confirmBtnText = "Yes, Cancel Batch";
    } else if (actionType === "resubmit" || actionType === true) {
      title = "Cancel & Resubmit Batch?";
      text = "This will cancel the current batch and submit a fresh batch job.";
      confirmBtnText = "Yes, Resubmit";
    } else {
      title = "Cancel & Run Online?";
      text = "This will cancel the batch and immediately evaluate papers via online API.";
      confirmBtnText = "Yes, Run Online";
    }

    const result = await swalConfirm(
      title,
      text,
      confirmBtnText,
      "No"
    );
    if (!result.isConfirmed) return;

    const isResubmit = actionType === "resubmit" || actionType === true;
    const isPureCancel = actionType === "pure_cancel";
    const loadingKey = isPureCancel ? "pure_cancel" : (isResubmit ? "resubmit" : "cancel");

    try {
      setActionLoading(loadingKey);
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/cancel-batch-job`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            batchId,
            resubmitToBatch: isResubmit,
            cancelOnly: isPureCancel,
          }),
          credentials: "include",
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to cancel batch job");

      swalSuccess(result.message || "Batch job action successful");
      setIsBatchIdModalOpen(false);
      fetchBatchJobs();
    } catch (err) {
      swalError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return "badge-requested";
      case "Completed":
        return "badge-graded";
      case "Failed":
        return "badge-review";
      default:
        return "badge-default";
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case "Grader":
        return "badge-pending";
      case "ScoreSummarizer":
        return "badge-requested";
      default:
        return "badge-default";
    }
  };

  const getProviderBadgeClass = (provider) => {
    const prov = (provider || "gemini").toLowerCase();
    if (prov.includes("openai")) return "badge-provider-openai";
    return "badge-provider-gemini";
  };

  const columns = [
    {
      key: "instituteName",
      label: "Institute",
      sortable: true,
    },
    {
      key: "classLevel",
      label: "Level",
      sortable: true,
    },
    {
      key: "className",
      label: "Class",
      sortable: true,
    },
    {
      key: "testName",
      label: "Test",
      sortable: true,
    },
    {
      key: "batchId",
      label: "Batch ID",
      sortable: true,
      render: (row) => (
        <span
          className="batch-id-link"
          onClick={() => showBatchId(row.batchId)}
          title="Click to view full Batch ID"
          style={{
            maxWidth: "100px",
            display: "inline-block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.textDecoration = "underline";
            e.currentTarget.style.color = "var(--color-1)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.textDecoration = "none";
            e.currentTarget.style.color = "inherit";
          }}
        >
          {row.batchId ? row.batchId.replace("batches/", "") : "N/A"}
        </span>
      ),
    },
    {
      key: "type",
      label: "Type",
      sortable: true,
      render: (row) => (
        <span className={`status-badge ${getTypeBadgeClass(row.type)}`}>
          {row.type || "N/A"}
        </span>
      ),
    },
    {
      key: "provider",
      label: "Provider",
      sortable: true,
      render: (row) => {
        const providerName = (row.provider || "gemini").toLowerCase();
        return (
          <span className={`status-badge ${getProviderBadgeClass(providerName)}`}>
            {providerName}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <span className={`status-badge ${getStatusBadgeClass(row.status)}`}>
          {row.status}
        </span>
      ),
    },
    {
      key: "answerPaperIds",
      label: "Answer Papers",
      sortable: false,
      render: (row) => (
        <span className="token-count">
          {row.answerPaperIds ? row.answerPaperIds.length : 0}
        </span>
      ),
    },
    {
      key: "scoreIds",
      label: "Scores",
      sortable: false,
      render: (row) => (
        <span className="token-count">
          {row.scoreIds ? row.scoreIds.length : 0}
        </span>
      ),
    },
    {
      key: "uploadedFileNames",
      label: "Files",
      sortable: false,
      render: (row) => (
        <span className="token-count">
          {row.uploadedFileNames ? row.uploadedFileNames.length : 0}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      render: (row) => (
        <span>
          {row.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A"}
        </span>
      ),
    },
    {
      key: "updatedAt",
      label: "Updated At",
      sortable: true,
      render: (row) => (
        <span>
          {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "N/A"}
        </span>
      ),
    },
  ];

  return (
    <>
      <Navbar
        pageName={`Batch Jobs${institute ? `: ${institute.name}` : ""}`}
        activePage="Batch Jobs"
        breadcrumbs={breadcrumbs}
      />
      <div className="manage-token">
        <div className="controls-section">
          <div className="type-selector">
            <label>Status:</label>
            <div className="type-buttons">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  className={`type-btn ${selectedStatus === option.value ? "active" : ""
                    }`}
                  onClick={() => setSelectedStatus(option.value)}
                >
                  <span className="type-icon">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="type-selector" style={{ marginTop: "12px" }}>
            <label>Type:</label>
            <div className="type-buttons">
              {typeOptions.map((option) => (
                <button
                  key={option.value}
                  className={`type-btn ${selectedType === option.value ? "active" : ""
                    }`}
                  onClick={() => setSelectedType(option.value)}
                >
                  <span className="type-icon">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="stats-grid" style={{ marginTop: "16px" }}>
          <div className="stat-card">
            <div className="stat-icon total">⚡</div>
            <div className="stat-content">
              <h3>Total Jobs</h3>
              <p className="stat-number">{data.length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon input">🟢</div>
            <div className="stat-content">
              <h3>Active</h3>
              <p className="stat-number">
                {data.filter((d) => d.status === "Active").length}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon output">✅</div>
            <div className="stat-content">
              <h3>Completed</h3>
              <p className="stat-number">
                {data.filter((d) => d.status === "Completed").length}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon cost">❌</div>
            <div className="stat-content">
              <h3>Failed</h3>
              <p className="stat-number">
                {data.filter((d) => d.status === "Failed").length}
              </p>
            </div>
          </div>
        </div>

        <div className="table-section" style={{ marginTop: "16px" }}>
          {loading ? (
            <Loader style={{ fontSize: "2.2rem" }} />
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <h2>Error Loading Data</h2>
              <p>{error}</p>
              <button onClick={fetchBatchJobs} className="retry-btn">
                <span>🔄</span> Retry
              </button>
            </div>
          ) : (
            <Table
              data={data}
              columns={columns}
              caption={`Batch Jobs (${data.length})`}
            />
          )}
        </div>
      </div>

      <Modal
        isOpen={isBatchIdModalOpen}
        onClose={() => setIsBatchIdModalOpen(false)}
      >
        <h3>Batch ID</h3>
        <div className="question-content">
          <div
            style={{
              padding: "20px",
              background: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
              marginBottom: "20px",
              wordBreak: "break-all",
              fontSize: "1.1rem",
              lineHeight: "1.5",
            }}
          >
            {selectedBatchId ? selectedBatchId.replace("batches/", "") : "N/A"}
          </div>
        </div>
        <div className="modal-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            className="btn-submit"
            onClick={() => copyToClipboard(selectedBatchId)}
            title="Copy to clipboard"
          >
            <i className="fa-solid fa-copy"></i> Copy to Clipboard
          </button>

          <button
            className="btn-submit"
            style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', opacity: actionLoading !== null ? 0.7 : 1, cursor: actionLoading !== null ? 'not-allowed' : 'pointer' }}
            onClick={() => handleCancelBatch(selectedBatchId, false)}
            title="Cancel with online"
            disabled={actionLoading !== null}
          >
            {actionLoading === "cancel" ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-ban"></i>} Cancel (Online)
          </button>

          <button
            className="btn-submit"
            style={{ backgroundColor: '#ffc107', color: '#fff', border: 'none', opacity: actionLoading !== null ? 0.7 : 1, cursor: actionLoading !== null ? 'not-allowed' : 'pointer' }}
            onClick={() => handleCancelBatch(selectedBatchId, true)}
            title="Cancel and resubmit to batch"
            disabled={actionLoading !== null}
          >
            {actionLoading === "resubmit" ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-rotate-right"></i>} Cancel & Resubmit batch
          </button>

          <button
            className="btn-submit"
            style={{ backgroundColor: '#495057', color: '#fff', border: 'none', opacity: actionLoading !== null ? 0.7 : 1, cursor: actionLoading !== null ? 'not-allowed' : 'pointer' }}
            onClick={() => handleCancelBatch(selectedBatchId, "pure_cancel")}
            title="Permanently cancel batch and drop queued requests"
            disabled={actionLoading !== null}
          >
            {actionLoading === "pure_cancel" ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-circle-xmark"></i>} Cancel Batch
          </button>
        </div>
      </Modal>
    </>
  );
}
